<script setup>
import axios from 'axios'
import { reactive, computed, onMounted } from 'vue'
import dayjs from 'dayjs'
import updateLocale from 'dayjs/plugin/updateLocale'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import Calendar from 'primevue/calendar'
import AutoComplete from 'primevue/autocomplete'
import Dialog from 'primevue/dialog'
import ProgressBar from 'primevue/progressbar'
import SelectButton from 'primevue/selectbutton'
import Swal from 'sweetalert2'
import format34 from '@/helpers/format34'
import { formatDatetime } from '../functions/format_works.js'

dayjs.extend(updateLocale)
dayjs.extend(isSameOrBefore)
dayjs.extend(isToday)
dayjs.extend(format34)
dayjs.updateLocale('en', {
  weekStart: 1,
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
})

const props = defineProps({
  creator: {
    type: Object,
    required: true
  },
  creatorSelectable: {
    type: Boolean,
    required: true
  },
  shiftOptions: {
    type: Array,
    required: true
  },
  month: {
    type: String,
    required: true
  }
})
const data = reactive({
  startOfCalendar: {},
  endOfCalendar: {},
  indexOfCalendar: {},
  fetched: {},
  displayWorks: [],
  isLoading: false,
  displayIndexDate: props.month ? new Date(props.month) : new Date(),
  displayedStatus: 'weekly'
})
const setCalendarData = () => {
  data.startOfCalendar = dayjs(data.displayIndexDate)
    .startOf('month')
    .startOf('week')
  data.endOfCalendar = dayjs(data.displayIndexDate).endOf('month').endOf('week')
  data.indexOfCalendar = dayjs().startOf('week')
}

const fetchWorks = async () => {
  let result = await axios.get(
    `/creators/${
      props.creator.id
    }/works.json?start_date=${data.startOfCalendar.format(
      'YYYY-MM-DD'
    )}&end_date=${data.endOfCalendar.format('YYYY-MM-DD')}`
  )
  data.fetched = result.data
}
const buildDisplayWorks = () => {
  const result = []
  let indexDay = data.startOfCalendar.clone()
  while (indexDay.isSameOrBefore(data.endOfCalendar)) {
    if (
      data.fetched.works.find(
        (work) => work.date === indexDay.format('YYYY-MM-DD')
      )
    )
      result.push(
        data.fetched.works.find(
          (work) => work.date === indexDay.format('YYYY-MM-DD')
        )
      )
    else {
      result.push({
        date: indexDay.format('YYYY-MM-DD'),
        status: 'adjusting',
        offer: null,
        offer_note: '',
        entry: null,
        entry_note: ''
      })
    }

    indexDay = indexDay.add(1, 'day')
  }
  return result
}

const dayBgColor = (day) => {
  if (isRegistrationInvalidDay(day.date)) {
    switch (day.status) {
      case 'adjusting':
        return 'bg-[#e4e2d0]'
      case 'monthly_fixed':
        return 'bg-[#d7dde5]'
      case 'weekly_fixed':
        return 'bg-[#d8e3db]'
      default:
        return 'bg-gray-50 text-gray-500'
    }
  }

  switch (day.status) {
    case 'adjusting':
      return 'bg-yellow-50'
    case 'monthly_fixed':
      return 'bg-blue-50'
    case 'weekly_fixed':
      return 'bg-green-50'
    default:
      return 'bg-gray-50 text-gray-500'
  }
}

const shiftBgColor = (day, type_id) => {
  if (day[type_id] == null) {
    return 'bg-gray-100'
  }
  if (type_id === 'offer_id') return 'bg-orange-300'
  if (type_id === 'entry_id') return 'bg-red-300'
}

const displayShift = (shiftId) => {
  const shift = props.shiftOptions.find(({ id }) => id === shiftId)
  if (shift) return shift.display_name

  return null
}

const updateCalendar = async () => {
  data.isLoading = true
  await setCalendarData()
  await fetchWorks()
  data.displayWorks = buildDisplayWorks()
  data.isLoading = false
}

onMounted(() => {
  updateCalendar()
  if (props.creatorSelectable) {
    initCreatorOptions()
  }
})

const isRegistrationInvalidDay = (date) => {
  return dayjs(date).isBefore(dayjs(), 'day')
}
const initCreatorOptions = async () => {
  let result = await axios.get(`/departments/primary_creators.json`)
  creatorForm.options = result.data
  creatorForm.selected = result.data.find(
    (option) => option.id === props.creator.id
  )
}
const creatorForm = reactive({
  selected: null,
  options: [],
  filteredOptions: []
})
const searchCreator = (event) => {
  setTimeout(() => {
    if (!event.query.trim().length) {
      creatorForm.filteredOptions = [...creatorForm.options]
    } else {
      creatorForm.filteredOptions = creatorForm.options.filter((creator) => {
        return creator.label.toLowerCase().includes(event.query.toLowerCase())
      })
    }
  }, 250)
}
const redirect = (event) => {
  const month = dayjs(data.displayIndexDate).format('YYYY-MM')
  location.href = `/creators/${event.value.id}/schedules?month=${month}`
}
const additionalEntry = reactive({
  displayModal: false,
  buttonDisabled: false,
  showProgress: false
})

const additionalEntryPayload = computed(() => {
  return {
    work_registration: {
      needs_email_notification: true,
      works: data.fetched.registerable_additional_entry_works
    }
  }
})
const additionalEntrySubmit = async () => {
  additionalEntry.showProgress = true
  additionalEntry.buttonDisabled = true
  data.isLoading = true
  try {
    await axios.post(
      `/creators/${props.creator.id}/additional_work_registrations`,
      additionalEntryPayload.value
    )
    data.isLoading = false
    additionalEntry.displayModal = false
    await Swal.fire({
      icon: 'success',
      title: '追加作業日登録を受付しました',
      confirmButtonText: '戻る',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    await updateCalendar()
    additionalEntry.showProgress = false
    additionalEntry.buttonDisabled = false
  } catch (error) {
    additionalEntry.displayModal = false
    additionalEntry.showProgress = false
    additionalEntry.buttonDisabled = false
    console.error(error)
    await Swal.fire({
      icon: 'error',
      title: '追加作業日登録に失敗しました',
      text: '管理者にお問合せください',
      confirmButtonText: 'OK',
      confirmButtonColor: '#40BF6C',
      reverseButtons: true,
      allowOutsideClick: false
    })
    data.isLoading = false
  }
}
</script>

<template>
  <vue-element-loading :active="data.isLoading" is-full-screen />
  <div class="relative pb-10 pt-8">
    <div class="pb-6">
      <div class="mx-auto px-4 sm:px-6 md:px-8">
        <!-- Replace with your content -->
        <div class="lg:flex lg:h-full lg:flex-col">
          <header class="px-1 py-3">
            <div class="mb-4 flex gap-4 max-lg:flex-col lg:items-center">
              <div v-if="props.creatorSelectable" class="lg:w-1/3">
                <AutoComplete
                  v-model="creatorForm.selected"
                  :suggestions="creatorForm.filteredOptions"
                  :dropdown="true"
                  scroll-height="400px"
                  option-label="label"
                  @complete="searchCreator($event)"
                  @item-select="redirect($event)"
                />
              </div>
              <div v-else class="text-2xl font-semibold text-gray-800">
                {{ props.creator.name }}
              </div>
              <div>
                <Calendar
                  v-model="data.displayIndexDate"
                  input-id="monthpicker"
                  view="month"
                  date-format="yy年 m月"
                  class="w-full"
                  @date-select="updateCalendar()"
                />
              </div>
              <div>
                <button
                  class="ml-2 rounded-md border border-transparent bg-yellow-500 px-8 py-1.5 font-medium text-white shadow-sm hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:ring-offset-2"
                  @click="additionalEntry.displayModal = true"
                >
                  <div class="flex flex-row">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="h-6 w-6"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M12 4.5v15m7.5-7.5h-15"
                      />
                    </svg>
                    <div>追加作業日登録</div>
                  </div>
                </button>
                <!--追加作業日モーダル-->
                <Dialog
                  v-model:visible="additionalEntry.displayModal"
                  header="追加作業日"
                  :modal="true"
                  class="w-full lg:w-1/2"
                >
                  <div class="flow-root py-4">
                    <p class="text-sm text-gray-500">
                      追加希望する作業日について時間帯とコメントをご記入ください。
                      なお月間スケジュール確定後のため、ご希望に添えない場合がございますのでご了承ください。
                    </p>
                    <p
                      v-if="
                        data.fetched.registerable_additional_entry_works
                          .length === 0
                      "
                      class="my-4 flex justify-center font-bold"
                    >
                      登録できる追加作業日はありません
                    </p>
                    <div
                      v-for="work in data.fetched
                        .registerable_additional_entry_works"
                      :key="work.id"
                      class="my-2"
                    >
                      <div class="flex max-lg:flex-col lg:items-center">
                        <div class="mx-2 my-1 w-16">
                          {{ dayjs(work.date).format('M/D(ddd)') }}
                        </div>
                        <div class="mx-2 my-1">
                          <SelectButton
                            v-model="work.additional_entry_id"
                            :options="shiftOptions"
                            option-label="display_name"
                            option-value="id"
                            aria-labelledby="single"
                            class="p-button-creator"
                          />
                        </div>
                        <div class="mx-2 my-1 grow">
                          <textarea
                            v-model="work.additional_entry_note"
                            rows="2"
                            class="focus:border-indigo-500 focus:ring-indigo-500 block w-full rounded-md border border-gray-200 p-1 shadow-sm sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <template #footer>
                    <button
                      class="group w-24 whitespace-nowrap rounded border border-gray-500 bg-white p-2.5 font-medium text-gray-800 hover:border-transparent hover:bg-gray-500 hover:text-white"
                      @click="additionalEntry.displayModal = false"
                    >
                      キャンセル
                    </button>
                    <button
                      :class="[
                        additionalEntry.buttonDisabled ||
                        data.fetched.registerable_additional_entry_works
                          .length === 0
                          ? 'disabled:opacity-25'
                          : 'hover:bg-green-500 hover:text-white',
                        'ml-4 w-24 rounded bg-green-300 p-2 p-2.5 px-4 font-bold text-gray-800'
                      ]"
                      :disabled="
                        additionalEntry.buttonDisabled ||
                        data.fetched.registerable_additional_entry_works
                          .length === 0
                      "
                      @click="additionalEntrySubmit"
                    >
                      登録
                    </button>
                    <ProgressBar
                      v-if="additionalEntry.showProgress"
                      mode="indeterminate"
                      style="height: 0.5em"
                      class="mt-4"
                    />
                  </template>
                </Dialog>
              </div>
            </div>
            <div class="lg:flex lg:justify-between">
              <div
                class="sm:flex md:ml-3 md:inline-block md:items-center lg:flex lg:items-center"
              >
                <div
                  class="flex flex-wrap items-center whitespace-nowrap text-xs md:flex md:items-center"
                >
                  <div class="py-3">
                    <div
                      class="mr-2 ml-4 inline-block h-3 w-3 rounded-full bg-orange-300"
                    ></div>
                    <span class="text-gray-700">Lab.提案</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="mr-2 ml-5 inline-block h-3 w-3 rounded-full bg-red-300"
                    ></div>
                    <span class="text-gray-700">クリエイター提出</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="mr-2 ml-5 inline-block h-3 w-3 rounded-full bg-blue-300"
                    ></div>
                    <span class="text-gray-700">月間確定</span>
                  </div>
                  <div class="py-3">
                    <div
                      class="mr-2 ml-5 inline-block h-3 w-3 rounded-full bg-green-300"
                    ></div>
                    <span class="text-gray-700">週間確定</span>
                  </div>
                  <div
                    class="ml-5 h-8 w-16 border border-gray-300 bg-yellow-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >調整中</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-blue-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >月確</span
                    >
                  </div>
                  <div
                    class="ml-2 h-8 w-16 border border-gray-300 bg-green-50 opacity-80"
                  >
                    <span class="flex justify-center leading-8 text-gray-800"
                      >週確</span
                    >
                  </div>
                </div>
              </div>
              <div class="lg:flex lg:items-center">
                <input
                  id="weekly"
                  v-model="data.displayedStatus"
                  class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                  type="radio"
                  value="weekly"
                />
                <label class="mr-4 text-gray-700">週間確定を表示</label>
                <input
                  id="monthly"
                  v-model="data.displayedStatus"
                  class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                  type="radio"
                  value="monthly"
                />
                <label class="mr-4 text-gray-700">月間確定を表示</label>
                <input
                  id="adjusting"
                  v-model="data.displayedStatus"
                  class="text-indigo-600 focus:ring-indigo-500 mr-2 h-4 w-4 rounded border-gray-300 drop-shadow"
                  type="radio"
                  value="adjusting"
                />
                <label class="mr-4 text-gray-700">調整中を表示</label>
              </div>
            </div>
          </header>
          <div
            class="shadow ring-1 ring-gray-300 ring-opacity-80 lg:flex lg:flex-auto lg:flex-col"
          >
            <div
              class="sr-only grid grid-cols-7 gap-px border-b border-gray-300 border-opacity-80 bg-gray-300 bg-opacity-80 text-center text-xs font-semibold leading-6 text-gray-700 lg:not-sr-only lg:flex-none"
            >
              <div class="bg-white py-2">月</div>
              <div class="bg-white py-2">火</div>
              <div class="bg-white py-2">水</div>
              <div class="bg-white py-2">木</div>
              <div class="bg-white py-2">金</div>
              <div class="bg-white py-2 text-blue-400">土</div>
              <div class="bg-white py-2 text-red-400">日</div>
            </div>
            <!-- カレンダー内側 -->
            <div
              class="flex bg-gray-200 text-xs leading-6 text-gray-700 lg:flex-auto"
            >
              <div class="w-full lg:grid lg:grid-cols-7 lg:gap-px">
                <div
                  v-for="day in data.displayWorks"
                  :key="day.date"
                  :class="[dayBgColor(day), 'relative flex py-2 px-3 lg:block']"
                >
                  <div
                    class="flex flex-col pr-3 lg:flex-row lg:justify-between lg:pr-0"
                  >
                    <time
                      :datetime="day.date"
                      :class="
                        dayjs(day.date).isToday()
                          ? 'flex h-6 w-6 items-center justify-center rounded-full bg-yellow-500 p-4 font-bold text-white'
                          : 'font-bold'
                      "
                    >
                      {{ dayjs(day.date).format('M/D') }}
                    </time>
                    <div
                      class="not-sr-only lg:sr-only"
                      :class="{
                        'text-blue-400': dayjs(day.date).day() === 6,
                        'text-red-400': dayjs(day.date).day() === 0
                      }"
                    >
                      {{ dayjs(day.date).format('ddd') }}
                    </div>
                    <!--履歴ボタン-->
                    <WorkHistory :day-id="day.id" />
                  </div>
                  <!--カレンダー1日分-->
                  <!--週間確定後-->
                  <div
                    v-if="
                      data.displayedStatus === 'weekly' &&
                      day.status === 'weekly_fixed'
                    "
                  >
                    <div v-if="day.reservations?.length !== 0">
                      <div
                        v-for="reservation in day.reservations"
                        :key="reservation.id"
                      >
                        <div class="my-2 flex flex-col">
                          <div
                            class="m-1 inline-flex h-8 w-36 flex-none items-center justify-center rounded-md bg-green-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                          >
                            {{
                              formatDatetime(
                                dayjs(reservation.start_time),
                                dayjs(reservation.end_time)
                              )
                            }}
                          </div>
                          <div class="flex flex-col pl-2.5 text-sm">
                            <div
                              v-if="
                                reservation.studio_name ||
                                reservation.resources_name
                              "
                            >
                              【{{ reservation.studio_name
                              }}{{ reservation.resources_name }}】
                            </div>
                            <div
                              v-if="reservation.project_name"
                              class="font-semibold"
                            >
                              {{ reservation.project_name }}
                            </div>
                            <div v-if="reservation.reservation_name">
                              {{ reservation.reservation_name }}
                            </div>
                            <div v-if="reservation.project_customer">
                              （{{ reservation.project_customer }}）
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <div class="h-32" />
                    </div>
                  </div>
                  <!--月間確定-->
                  <div
                    v-else-if="
                      (data.displayedStatus === 'monthly' ||
                        data.displayedStatus === 'weekly') &&
                      (day.status === 'monthly_fixed' ||
                        day.status === 'weekly_fixed')
                    "
                  >
                    <div v-if="day.shift_time_type_id">
                      <div class="my-2 flex gap-x-2">
                        <div
                          class="inline-flex h-28 w-12 flex-none items-center rounded-md bg-blue-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                        >
                          {{ displayShift(day.shift_time_type_id) }}
                        </div>
                        <div class="flex items-center font-semibold">
                          {{ day.name }}
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <div class="my-2 flex gap-x-2">
                        <!-- 月間確定後の予定が空白かつ追加作業日が存在する時 -->
                        <div
                          v-if="day.additional_entry_id"
                          class="flex flex-col"
                        >
                          <div
                            class="my-2 inline-flex h-12 w-12 flex-none items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-white"
                          />
                          <!-- クリエイター追加作業希望日-->
                          <div class="flex items-center">
                            <div
                              class="my-2 inline-flex h-12 w-12 flex-none items-center rounded-md bg-red-300 px-2.5 py-0.5 text-sm font-semibold text-white"
                            >
                              {{ displayShift(day.additional_entry_id) }}
                            </div>
                            <div class="ml-2">
                              {{ day.additional_entry_note }}
                            </div>
                          </div>
                        </div>
                        <!-- 月間確定後の予定が空白の時 -->
                        <div
                          v-else
                          class="inline-flex h-28 w-12 flex-none items-center rounded-md bg-gray-100 px-2.5 py-0.5 text-sm font-semibold text-white"
                        ></div>
                      </div>
                    </div>
                  </div>
                  <!--調整中-->
                  <div v-else>
                    <div class="my-2 flex gap-x-2">
                      <!--Lab依頼-->
                      <div
                        :class="[
                          shiftBgColor(day, 'offer_id'),
                          'inline-flex h-12 w-12 flex-none items-center rounded-md px-2.5 py-0.5 text-sm font-semibold text-white'
                        ]"
                      >
                        {{ displayShift(day.offer_id) }}
                      </div>
                      <!--  Labメモ -->
                      <div class="editable-text">
                        {{ day.offer_note }}
                        {{ displayShift(day.shift_time_type_id) }}
                      </div>
                    </div>
                    <div class="my-2 flex gap-x-2">
                      <!-- クリエイター希望-->
                      <div
                        :class="[
                          shiftBgColor(day, 'entry_id'),
                          'inline-flex h-12 w-12 flex-none items-center rounded-md px-2.5 py-0.5 text-sm font-semibold text-white'
                        ]"
                      >
                        {{ displayShift(day.entry_id) }}
                      </div>
                      <!-- クリエイターメモ -->
                      <div class="editable-text">
                        {{ day.entry_note }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- /End replace -->
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-button-history.p-button.p-button-icon-only.p-button-rounded) {
  height: 1.5rem;
  width: 1.5rem;
}

:deep(.p-calendar) {
  .p-inputtext:enabled:focus {
    border-color: #f7d070;
    box-shadow: 0 0 0 1px #f7d070;
  }
}

:deep(.p-autocomplete) {
  width: 100%;

  .p-button,
  .p-highlight {
    background: #b1b1b1;
    border: #f7d070;
  }

  .p-button:focus,
  .p-inputtext:enabled:focus {
    border-color: #f7d070;
    box-shadow: 0 0 0 1px #f7d070;
  }
}

:deep(.p-button-creator.p-selectbutton) {
  .p-button.p-highlight {
    background: #e66a6a;
    border-color: #e66a6a;
  }

  .p-button:focus {
    box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px #e66a6a,
      0 1px 2px 0 rgba(0, 0, 0, 0);
  }
}
</style>
